/**
 * Human-in-the-loop task list — `GET /tasks`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference): the grid
 * owns paging, search, sorting and filtering, so the page keeps only the column plan, the row
 * action, and the one piece of refetch state (`listVersion`).
 *
 * NOTE: `ROUTES.EXTERNAL_TASK_SYSTEMS` also renders this component today (a known route-table bug
 * that is out of scope here). Nothing below assumes the current path, so both routes behave the
 * same — do not add path-dependent logic without fixing the route table first.
 *
 * ── Column plan ──────────────────────────────────────────────────────────────────────────────────
 *  field                    | header      | width | cell type          | filter
 *  -------------------------|-------------|-------|--------------------|-----------------------------
 *  title                    | Title       |  28%  | text (clickable)   | toolbar search (searchTerm)
 *  externalTaskSystemName   | System      |  16%  | text               | —
 *  taskType                 | Type        |  13%  | custom → Chip      | popover select (taskType)
 *  status                   | Status      |  13%  | custom → Chip      | status tabs + popover select
 *  assignedTo               | Assigned To |  16%  | text (valueGetter) | popover text (assignedTo)
 *  dueDate                  | Due         |  14%  | date (locale)      | —
 *
 * Dropped (present on the old MUI table, deliberately not carried over — six columns is the cap and
 * these are all on the task detail page): `description` (was a muted second line under the title),
 * `createdAt` (`dueDate` is the date a work queue is actually read on), `completedAt`,
 * `externalTaskId`, `agentSessionId`, `safetyGateExecutionId`.
 *
 * Service filters NOT offered: `externalTaskSystemId`, `agentSessionId` and `safetyGateExecutionId`
 * are accepted by `hitlService.getTasks` but are raw GUIDs — useless to type into a filter box, so
 * they are left to deep links. `hitlService.getTasks` accepts no sort parameters at all, which is
 * why no column is marked `sortable`: a sort control the endpoint ignores is worse than none.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { KanbanSquare, List, Plus, RefreshCw } from 'lucide-react';

import { createCortexPagedDataSource } from '../components/data-display/cortexPagedDataSource';
import { hitlService, TaskStatus, TaskType, type ExternalTaskDto } from '../services/hitlService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import './TaskListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to `paramKey`, and it builds each tab's test id
 * as `cordio-datatable-status-tab-<value>`. The All tab therefore declares the literal `'all'` —
 * an empty value would emit `cordio-datatable-status-tab-` and GRID-13 would select nothing.
 *
 * Because `'all'` is a UI concept the API rejects, `paramKey` is the synthetic
 * {@link STATUS_TAB_PARAM_KEY} and {@link toTaskQuery} maps it onto the real `status` filter.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: TaskStatus.Pending },
  { label: 'In Progress', value: TaskStatus.InProgress },
  { label: 'Completed', value: TaskStatus.Completed },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'statusTab';

/**
 * The full `TaskStatus` set, offered in the filter popover under the SAME key as the tabs.
 *
 * WHY one key rather than a second `status` filter: the old page had a status dropdown covering all
 * six statuses, and the four tabs above only reach three of them. Pointing both surfaces at
 * `statusTab` keeps that reach without inventing a precedence rule — picking `Rejected` in the
 * popover simply leaves no tab highlighted, and picking a tab updates the popover.
 */
const STATUS_FILTER_OPTIONS = Object.values(TaskStatus).map((status) => ({
  label: status === TaskStatus.InProgress ? 'In Progress' : status,
  value: status,
}));

const TASK_TYPE_FILTER_OPTIONS = Object.values(TaskType).map((taskType) => ({
  label: taskType,
  value: taskType,
}));

/** Statuses shown as board columns, in the order a task moves through them. */
const BOARD_COLUMNS = [
  { status: TaskStatus.Pending, label: 'Pending' },
  { status: TaskStatus.InProgress, label: 'In Progress' },
  { status: TaskStatus.Approved, label: 'Approved' },
  { status: TaskStatus.Completed, label: 'Completed' },
  { status: TaskStatus.Rejected, label: 'Rejected' },
] as const;

/** How many tasks the board view loads. The board has no paging, so it is a hard ceiling. */
const BOARD_PAGE_SIZE = 100;

type ViewMode = 'list' | 'board';

const statusChipColor = (status: TaskStatus): ChipColor => {
  switch (status) {
    case TaskStatus.Approved:
    case TaskStatus.Completed:
      return 'green';
    case TaskStatus.Pending:
      return 'amber';
    case TaskStatus.InProgress:
      return 'blue';
    case TaskStatus.Rejected:
      return 'red';
    default:
      return 'default';
  }
};

const taskTypeChipColor = (taskType: TaskType): ChipColor => {
  switch (taskType) {
    case TaskType.Approval:
      return 'accent';
    case TaskType.Review:
      return 'blue';
    case TaskType.Action:
      return 'amber';
    default:
      return 'default';
  }
};

/**
 * Turns the grid's query into the one `hitlService.getTasks` accepts: the synthetic status-tab value
 * becomes `status`, and All becomes "no filter at all" rather than a third state.
 */
function toTaskQuery(query: Record<string, unknown>): Record<string, unknown> {
  const { [STATUS_TAB_PARAM_KEY]: statusTab, ...rest } = query;

  if (typeof statusTab === 'string' && statusTab !== 'all') {
    return { ...rest, status: statusTab as TaskStatus };
  }
  return rest;
}

const taskDetailPath = (id: string) => ROUTES.TASK_DETAIL.replace(':id', id);

export const TaskListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  /** Bumped to rebuild `dataSource` and make the grid (or the board) refetch. */
  const [listVersion, setListVersion] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  /**
   * Board rows. Kept as page state because the board is not a grid: it needs every status at once,
   * which `CordioAbpPagedDataTable` cannot express.
   */
  const [boardTasks, setBoardTasks] = useState<readonly ExternalTaskDto[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);

  const dataSource = useMemo(
    () => createCortexPagedDataSource<ExternalTaskDto>((query) => hitlService.getTasks(toTaskQuery(query))),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  useEffect(() => {
    if (viewMode !== 'board') return;

    let cancelled = false;
    setBoardLoading(true);

    hitlService
      .getTasks({ pageNumber: 1, pageSize: BOARD_PAGE_SIZE })
      .then((result) => {
        if (!cancelled) setBoardTasks(result.items);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        addNotification(err instanceof Error ? err.message : 'Failed to load tasks', 'error');
      })
      .finally(() => {
        if (!cancelled) setBoardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [viewMode, listVersion, addNotification]);

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ExternalTaskDto>>>(
    () => [
      { field: 'title', header: 'Title', type: 'text', width: '28%', clickable: true },
      { field: 'externalTaskSystemName', header: 'System', type: 'text', width: '16%' },
      {
        field: 'taskType',
        header: 'Type',
        type: 'custom',
        width: '13%',
        customCell: (row) => <Chip label={row.taskType} color={taskTypeChipColor(row.taskType)} />,
      },
      {
        field: 'status',
        header: 'Status',
        type: 'custom',
        width: '13%',
        // A Chip rather than the built-in `status` cell so the colours match the tabs above the
        // grid. The label is the API's own value, never a rewording of it.
        customCell: (row) => <Chip label={row.status} color={statusChipColor(row.status)} />,
      },
      {
        field: 'assignedTo',
        header: 'Assigned To',
        type: 'text',
        width: '16%',
        // The API returns a user id, not a name. A shortened id beats a 36-character GUID, and
        // "Unassigned" is clearer than the grid's em-dash placeholder for a queue.
        valueGetter: (row) => (row.assignedTo ? `User ${row.assignedTo.slice(0, 8)}…` : 'Unassigned'),
      },
      { field: 'dueDate', header: 'Due', type: 'date', width: '14%', dateFormat: 'locale' },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search tasks by title' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Task status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        // Same key as the tabs on purpose — see STATUS_FILTER_OPTIONS.
        {
          key: STATUS_TAB_PARAM_KEY,
          label: 'Status',
          type: 'select',
          options: STATUS_FILTER_OPTIONS,
          paramLabel: 'status',
        },
        { key: 'taskType', label: 'Type', type: 'select', options: TASK_TYPE_FILTER_OPTIONS },
        { key: 'assignedTo', label: 'Assigned To', type: 'text', placeholder: 'User id' },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ExternalTaskDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View task details',
        onClick: (row) => navigate(taskDetailPath(row.id)),
      },
    ],
    [navigate],
  );

  const handleRefresh = useCallback(() => setListVersion((v) => v + 1), []);

  return (
    <div className="task-list">
      <ShellHeader
        title="Tasks"
        description="Human-in-the-loop tasks raised in connected task systems, with their approval state."
        actions={
          <div className="task-list__header-actions">
            <div className="task-list__view-toggle" role="group" aria-label="Task view">
              <Button
                type="button"
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                data-testid="task-list-view-list"
              >
                <List className="h-4 w-4 shrink-0" aria-hidden />
                <span className="sr-only">List view</span>
              </Button>
              <Button
                type="button"
                variant={viewMode === 'board' ? 'default' : 'outline'}
                size="sm"
                aria-pressed={viewMode === 'board'}
                onClick={() => setViewMode('board')}
                data-testid="task-list-view-board"
              >
                <KanbanSquare className="h-4 w-4 shrink-0" aria-hidden />
                <span className="sr-only">Board view</span>
              </Button>
            </div>

            {viewMode === 'board' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={boardLoading}
                onClick={handleRefresh}
              >
                <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
                Refresh
              </Button>
            )}

            <Button
              type="button"
              variant="default"
              size="sm"
              className="gap-2"
              onClick={() => navigate(ROUTES.TASK_NEW)}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Create Task
            </Button>
          </div>
        }
      />

      {viewMode === 'list' ? (
        <CordioAbpPagedDataTable<ExternalTaskDto>
          className="task-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(taskDetailPath(row.id))}
          onCellClick={({ row }) => navigate(taskDetailPath(row.id))}
        />
      ) : (
        <div className="task-list__board" data-testid="task-list-board">
          {BOARD_COLUMNS.map((column) => {
            const columnTasks = boardTasks.filter((task) => task.status === column.status);
            return (
              <section className="task-list__board-column" key={column.status}>
                <header className="task-list__board-header">
                  <h2 className="task-list__board-title">{column.label}</h2>
                  <Chip label={String(columnTasks.length)} color="default" />
                </header>
                <div className="task-list__board-body">
                  {columnTasks.length === 0 ? (
                    <p className="task-list__board-empty">No tasks</p>
                  ) : (
                    columnTasks.map((task) => (
                      <button
                        type="button"
                        className="task-list__card"
                        key={task.id}
                        onClick={() => navigate(taskDetailPath(task.id))}
                      >
                        <span className="task-list__card-title">{task.title}</span>
                        <span className="task-list__card-meta">
                          <Chip label={task.taskType} color={taskTypeChipColor(task.taskType)} />
                          <span className="task-list__card-system">{task.externalTaskSystemName}</span>
                        </span>
                        {task.dueDate && (
                          <span className="task-list__card-due">
                            Due {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};
